import CreateForm from "@/pages/parameters/CreateForm";
import type {
  FieldDecisionRulesConfig,
  Field,
  FieldScoringConfig,
  FieldType,
  Section,
  Step,
} from "@/pages/parameters/ParametersForms";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Check,
  CirclePlus,
  GripVertical,
  Settings2,
  SquarePen,
  Trash,
} from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";

const INTRO_STEP_API_NAME = "introduction";
const INTRO_STEP_LABEL = "introduction";
const PROTECTED_STEP_API_NAME = "descriptif_du_projet";
const PROTECTED_STEP_LABEL = "descriptif du projet";
const LOCKED_FINANCING_STEP_API_NAME = "financement";
const LOCKED_FINANCING_STEP_LABEL = "financement";
const PROTECTED_DESCRIPTIF_FIELDS = [
  "name",
  "description",
  "type",
  "launchdate",
  "expectedduration",
  "country",
  "searchfinancinginternal",
];
const HIDDEN_CALL_DESCRIPTIF_FIELDS = [
  "name",
  "description",
  "type",
  "projecttype",
  "launchdate",
  "expectedduration",
  "country",
  "searchfinancinginternal",
];

type VisibilityMode = "all" | "show_only" | "hide_only";

type UpsertFieldPayload = {
  name: string;
  label: string;
  type: FieldType;
  required: boolean;
  options?: unknown;
  nativeField?: string;
  step: number;
  parentId?: number;
};

export type ProjectTypeOption = {
  id: number;
  label: string;
};

type CallFormScoringDecisionMode =
  | "auto_threshold"
  | "auto_total"
  | "manual_total";

type CallFormScoringSettings = {
  enabled: boolean;
  decisionMode?: CallFormScoringDecisionMode;
  acceptancePercentage?: number | null;
  rejectionPercentage?: number | null;
};

const CALL_FORM_SCORING_SETTINGS_KEY = "callFormScoringSettings";

const extractChoiceOptions = (rawOptions: unknown): string[] | null => {
  if (Array.isArray(rawOptions)) {
    return rawOptions.map((option) => String(option));
  }

  if (
    rawOptions &&
    typeof rawOptions === "object" &&
    !Array.isArray(rawOptions) &&
    Array.isArray((rawOptions as Record<string, unknown>).choices)
  ) {
    return ((rawOptions as Record<string, unknown>).choices as unknown[]).map(
      (option) => String(option),
    );
  }

  return null;
};

const extractScoringConfig = (rawOptions: unknown): FieldScoringConfig | null => {
  if (
    !rawOptions ||
    typeof rawOptions !== "object" ||
    Array.isArray(rawOptions) ||
    !("scoring" in (rawOptions as Record<string, unknown>))
  ) {
    return null;
  }

  const scoring = (rawOptions as Record<string, unknown>).scoring;
  if (!scoring || typeof scoring !== "object" || Array.isArray(scoring)) {
    return null;
  }

  const modeRaw = String((scoring as Record<string, unknown>).mode ?? "");
  if (modeRaw !== "auto_choice" && modeRaw !== "manual") {
    return null;
  }

  const pointsByChoice =
    (scoring as Record<string, unknown>).pointsByChoice &&
    typeof (scoring as Record<string, unknown>).pointsByChoice === "object" &&
    !Array.isArray((scoring as Record<string, unknown>).pointsByChoice)
      ? Object.fromEntries(
          Object.entries(
            (scoring as Record<string, unknown>).pointsByChoice as Record<
              string,
              unknown
            >,
          ).map(([key, value]) => [key, Number(value ?? 0)]),
        )
      : undefined;

  return {
    enabled: Boolean((scoring as Record<string, unknown>).enabled),
    mode: modeRaw,
    maxPoints:
      (scoring as Record<string, unknown>).maxPoints !== undefined &&
      (scoring as Record<string, unknown>).maxPoints !== null
        ? Number((scoring as Record<string, unknown>).maxPoints)
        : null,
    pointsByChoice,
  };
};

const extractDecisionRulesConfig = (
  rawOptions: unknown,
): FieldDecisionRulesConfig | null => {
  if (
    !rawOptions ||
    typeof rawOptions !== "object" ||
    Array.isArray(rawOptions) ||
    !("decisionRules" in (rawOptions as Record<string, unknown>))
  ) {
    return null;
  }

  const decisionRules = (rawOptions as Record<string, unknown>).decisionRules;
  if (
    !decisionRules ||
    typeof decisionRules !== "object" ||
    Array.isArray(decisionRules)
  ) {
    return null;
  }

  const disqualifyingChoices = Array.isArray(
    (decisionRules as Record<string, unknown>).disqualifyingChoices,
  )
    ? ((decisionRules as Record<string, unknown>).disqualifyingChoices as unknown[])
        .map((choice) => String(choice).trim())
        .filter(Boolean)
    : [];

  if (disqualifyingChoices.length === 0) {
    return null;
  }

  return {
    disqualifyingChoices,
  };
};

const serializeFieldOptions = (field: Field): unknown => {
  const hasChoices = Array.isArray(field.options) && field.options.length > 0;
  const hasScoring = Boolean(field.scoring?.enabled);
  const hasDecisionRules =
    (field.decisionRules?.disqualifyingChoices?.length ?? 0) > 0;

  if (hasChoices && (hasScoring || hasDecisionRules)) {
    return {
      choices: field.options,
      ...(hasScoring ? { scoring: field.scoring } : {}),
      ...(hasDecisionRules ? { decisionRules: field.decisionRules } : {}),
    };
  }

  if (hasChoices) {
    return field.options;
  }

  if (hasScoring || hasDecisionRules) {
    return {
      ...(hasScoring ? { scoring: field.scoring } : {}),
      ...(hasDecisionRules ? { decisionRules: field.decisionRules } : {}),
    };
  }

  return null;
};

const normalizeKey = (value?: string | null) =>
  (value ?? "").trim().toLowerCase();

const toApiStepName = (label: string, fallbackStep: number): string => {
  const trimmed = label.trim();
  if (!trimmed) return `step_${fallbackStep}`;
  const cleaned = trimmed
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return cleaned || `step_${fallbackStep}`;
};

const isIntroStep = (step?: Step) =>
  normalizeKey(step?.apiName) === INTRO_STEP_API_NAME ||
  normalizeKey(step?.nameStep) === INTRO_STEP_LABEL ||
  normalizeKey(step?.type) === "intro";

const isProtectedStep = (step?: Step) =>
  normalizeKey(step?.apiName) === PROTECTED_STEP_API_NAME ||
  normalizeKey(step?.nameStep) === PROTECTED_STEP_LABEL;

const isLockedFinancingStep = (step?: Step) =>
  normalizeKey(step?.apiName) === LOCKED_FINANCING_STEP_API_NAME ||
  normalizeKey(step?.nameStep) === LOCKED_FINANCING_STEP_LABEL;

const isStepLockedForEdition = (step?: Step) =>
  isIntroStep(step);

const isStepLockedForMove = (step?: Step) =>
  isIntroStep(step);

const getCallFormScoringSettings = (
  steps: Step[],
): CallFormScoringSettings => {
  const introStep = steps.find((step) => isIntroStep(step));
  const raw =
    introStep?.options &&
    typeof introStep.options === "object" &&
    !Array.isArray(introStep.options)
      ? (introStep.options as Record<string, unknown>)[
          CALL_FORM_SCORING_SETTINGS_KEY
        ]
      : null;

  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { enabled: false };
  }

  const decisionModeRaw = String(
    (raw as Record<string, unknown>).decisionMode ?? "",
  );
  const acceptancePercentageRaw = (raw as Record<string, unknown>).acceptancePercentage;
  const rejectionPercentageRaw = (raw as Record<string, unknown>).rejectionPercentage;

  return {
    enabled: Boolean((raw as Record<string, unknown>).enabled),
    decisionMode:
      decisionModeRaw === "auto_threshold" ||
      decisionModeRaw === "auto_auto_only" ||
      decisionModeRaw === "auto_total" ||
      decisionModeRaw === "manual_total"
        ? ((decisionModeRaw === "auto_auto_only" ? "auto_threshold" : decisionModeRaw) as CallFormScoringDecisionMode)
        : undefined,
    acceptancePercentage:
      acceptancePercentageRaw !== undefined && acceptancePercentageRaw !== null
        ? Number(acceptancePercentageRaw)
        : null,
    rejectionPercentage:
      rejectionPercentageRaw !== undefined && rejectionPercentageRaw !== null
        ? Number(rejectionPercentageRaw)
        : null,
  };
};

export const buildInitializePayloadFromDraft = (steps: Step[]) => ({
  steps: [...steps]
    .sort((a, b) => a.step - b.step)
    .map((step, stepIndex) => ({
      step: stepIndex + 1,
      name: step.apiName ?? toApiStepName(step.nameStep, stepIndex + 1),
      label: step.nameStep,
      description: step.description ?? null,
      options: step.options ?? null,
      type: isIntroStep(step) ? "intro" : "step",
      fields: [...(step.fields ?? [])]
        .sort((a, b) => (a.step ?? 0) - (b.step ?? 0))
        .map((field, fieldIndex) => ({
          name: field.name,
          label: field.label,
          type: field.type,
          required: Boolean(field.required),
          options: serializeFieldOptions(field),
          nativeField: field.nativeField ?? null,
          step: fieldIndex + 1,
        })),
      sections: [...(step.sections ?? [])]
        .sort((a, b) => (a.step ?? 0) - (b.step ?? 0))
        .map((section, sectionIndex) => ({
          nameSection: section.name,
          label: section.label,
          description: section.description ?? null,
          step: sectionIndex + 1,
          fields: [...(section.fields ?? [])]
            .sort((a, b) => (a.step ?? 0) - (b.step ?? 0))
            .map((field, fieldIndex) => ({
              name: field.name,
              label: field.label,
              type: field.type,
              required: Boolean(field.required),
              options: serializeFieldOptions(field),
              nativeField: field.nativeField ?? null,
              step: fieldIndex + 1,
            })),
        })),
    })),
});

export const hydrateDraftSteps = (source: Step[]): Step[] => {
  let localId = 1000;
  const nextId = () => {
    localId += 1;
    return localId;
  };

  return source.map((rawStep, stepIndex) => {
    const stepId = nextId();
    return {
      id: stepId,
      step: stepIndex + 1,
      nameStep: rawStep.nameStep,
      description: rawStep.description ?? "",
      options: rawStep.options ?? null,
      apiName:
        normalizeKey(rawStep.nameStep) === INTRO_STEP_LABEL
          ? INTRO_STEP_API_NAME
          : toApiStepName(rawStep.nameStep, stepIndex + 1),
      type:
        normalizeKey(rawStep.nameStep) === INTRO_STEP_LABEL ? "intro" : "step",
      visibilityMode: rawStep.visibilityMode ?? "all",
      visibilityProjectTypeIds: rawStep.visibilityProjectTypeIds ?? [],
      fields: (rawStep.fields ?? []).map((field, fieldIndex) => ({
        ...field,
        options: extractChoiceOptions(field.options),
        scoring: extractScoringConfig(field.options),
        decisionRules: extractDecisionRulesConfig(field.options),
        id: nextId(),
        step: field.step ?? fieldIndex + 1,
        parentId: stepId,
        sectionId: null,
        visibilityMode: field.visibilityMode ?? "all",
        visibilityProjectTypeIds: field.visibilityProjectTypeIds ?? [],
      })),
      sections: (rawStep.sections ?? []).map((section, sectionIndex) => {
        const sectionId = nextId();
        return {
          ...section,
          id: sectionId,
          name:
            (section as any).name ??
            (section as any).nameSection ??
            toApiStepName(section.label, sectionIndex + 1),
          step: section.step ?? sectionIndex + 1,
          fields: (section.fields ?? []).map((field, fieldIndex) => ({
            ...field,
            options: extractChoiceOptions(field.options),
            scoring: extractScoringConfig(field.options),
            decisionRules: extractDecisionRulesConfig(field.options),
            id: nextId(),
            step: field.step ?? fieldIndex + 1,
            parentId: sectionId,
            sectionId,
            visibilityMode: field.visibilityMode ?? "all",
            visibilityProjectTypeIds: field.visibilityProjectTypeIds ?? [],
          })),
        } as Section;
      }),
    } as Step;
  });
};

export default function CallSpecificFormBuilder({
  steps,
  selectedStep,
  setSelectedStep,
  setSteps,
  projectTypeOptions,
  projectTypeOptionsLoading,
  onModalVisibilityChange,
  allowAdvancedScoringModes = true,
}: {
  steps: Step[];
  selectedStep: number;
  setSelectedStep: (step: number) => void;
  setSteps: Dispatch<SetStateAction<Step[]>>;
  projectTypeOptions: ProjectTypeOption[];
  projectTypeOptionsLoading: boolean;
  onModalVisibilityChange?: (hidden: boolean) => void;
  allowAdvancedScoringModes?: boolean;
}) {
  const nextLocalIdRef = useRef(100000);
  const [editStep, setEditStep] = useState<number>(-1);
  const [stepLabelDraft, setStepLabelDraft] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [draggedStepId, setDraggedStepId] = useState<number | null>(null);
  const [dragOverStepId, setDragOverStepId] = useState<number | null>(null);
  const [scoringSettingsOpen, setScoringSettingsOpen] = useState(false);
  const [scoringSettingsDraft, setScoringSettingsDraft] =
    useState<CallFormScoringSettings>({ enabled: false });

  const allocateId = () => {
    nextLocalIdRef.current += 1;
    return nextLocalIdRef.current;
  };

  const selectedStepData = useMemo(
    () => steps.find((item) => item.step === selectedStep),
    [steps, selectedStep],
  );
  const currentCallFormScoringSettings = useMemo(() => {
    const settings = getCallFormScoringSettings(steps);
    if (!settings.enabled || allowAdvancedScoringModes) {
      return settings;
    }

    return {
      ...settings,
      decisionMode: "auto_threshold" as CallFormScoringDecisionMode,
    };
  }, [allowAdvancedScoringModes, steps]);
  const isCallFormScoringActive =
    currentCallFormScoringSettings.enabled ||
    (scoringSettingsOpen && scoringSettingsDraft.enabled);
  const currentScoringUsesManualDecision =
    currentCallFormScoringSettings.decisionMode === "manual_total";

  const fields = useMemo(() => {
    const currentStep = steps.find((item) => item.step === selectedStep);
    const rootFields = currentStep?.fields ?? [];
    const sectionFields = (currentStep?.sections ?? []).flatMap(
      (section) => section.fields ?? [],
    );
    return [...rootFields, ...sectionFields];
  }, [steps, selectedStep]);

  const sections = useMemo(
    () => selectedStepData?.sections ?? [],
    [selectedStepData],
  );

  const hasMapFieldInForm = useMemo(
    () =>
      steps.some(
        (step) =>
          step.fields.some((field) => field.type === "Map") ||
          (step.sections ?? []).some((section) =>
            section.fields.some((field) => field.type === "Map"),
          ),
      ),
    [steps],
  );

  const hasFundingFieldInForm = useMemo(
    () =>
      steps.some(
        (step) =>
          step.fields.some(
            (field) =>
              field.type === "funding" ||
              normalizeKey(field.nativeField) === "desiredfunding",
          ) ||
          (step.sections ?? []).some((section) =>
            section.fields.some(
              (field) =>
                field.type === "funding" ||
                normalizeKey(field.nativeField) === "desiredfunding",
            ),
          ),
      ),
    [steps],
  );

  useEffect(() => {
    if (!scoringSettingsOpen) return;
    setScoringSettingsDraft(currentCallFormScoringSettings);
  }, [currentCallFormScoringSettings, scoringSettingsOpen]);

  useEffect(() => {
    if (!allowAdvancedScoringModes) {
      setScoringSettingsDraft((current) => ({
        ...current,
        decisionMode: "auto_threshold",
      }));
    }
  }, [allowAdvancedScoringModes]);

  useEffect(() => {
    onModalVisibilityChange?.(scoringSettingsOpen);
    return () => {
      onModalVisibilityChange?.(false);
    };
  }, [onModalVisibilityChange, scoringSettingsOpen]);

  const updateStep = (stepId: number, updater: (step: Step) => Step) => {
    setSteps((current) =>
      current.map((step) => (step.id === stepId ? updater(step) : step)),
    );
  };

  const updateIntroStepOptions = (
    updater: (options: Record<string, unknown>) => Record<string, unknown>,
  ) => {
    setSteps((current) =>
      current.map((step) => {
        if (!isIntroStep(step)) {
          return step;
        }

        const baseOptions =
          step.options && typeof step.options === "object" && !Array.isArray(step.options)
            ? { ...(step.options as Record<string, unknown>) }
            : {};

        return {
          ...step,
          options: updater(baseOptions),
        };
      }),
    );
  };

  const findFieldById = (fieldId: number): Field | undefined => {
    if (!selectedStepData) return undefined;
    const rootField = selectedStepData.fields.find((field) => field.id === fieldId);
    if (rootField) return rootField;
    for (const section of selectedStepData.sections ?? []) {
      const sectionField = section.fields.find((field) => field.id === fieldId);
      if (sectionField) return sectionField;
    }
    return undefined;
  };

  const handleAddStep = async () => {
    const nextNumber = steps.length ? Math.max(...steps.map((item) => item.step)) + 1 : 1;
    const newStep: Step = {
      id: allocateId(),
      step: nextNumber,
      nameStep: "Nouvelle etape",
      apiName: toApiStepName("Nouvelle etape", nextNumber),
      description: "",
      type: "step",
      fields: [],
      sections: [],
      visibilityMode: "all",
      visibilityProjectTypeIds: [],
    };
    setSteps((current) => [...current, newStep]);
    setSelectedStep(nextNumber);
    setEditStep(nextNumber);
    setStepLabelDraft("Nouvelle etape");
  };

  const reorderSteps = (source: Step[], draggedId: number, targetId: number) => {
    const draggedIndex = source.findIndex((item) => item.id === draggedId);
    const targetIndex = source.findIndex((item) => item.id === targetId);
    if (draggedIndex < 0 || targetIndex < 0) return source;

    const next = [...source];
    const [moved] = next.splice(draggedIndex, 1);
    next.splice(targetIndex, 0, moved);

    return next.map((item, index) => ({
      ...item,
      step: index + 1,
    }));
  };

  const handleDropStep = (targetId: number) => {
    if (!draggedStepId || draggedStepId === targetId) {
      setDraggedStepId(null);
      setDragOverStepId(null);
      return;
    }

    const draggedStep = steps.find((item) => item.id === draggedStepId);
    const targetStep = steps.find((item) => item.id === targetId);
    if (isStepLockedForMove(draggedStep) || isStepLockedForMove(targetStep)) {
      setDraggedStepId(null);
      setDragOverStepId(null);
      return;
    }

    const reordered = reorderSteps(steps, draggedStepId, targetId);
    setSteps(reordered);
    const selectedStepAfterMove = reordered.find((item) => item.id === selectedStepData?.id);
    if (selectedStepAfterMove) {
      setSelectedStep(selectedStepAfterMove.step);
    }
    setDraggedStepId(null);
    setDragOverStepId(null);
  };

  const handleDeleteStep = async (stepToDelete: Step) => {
    if (isStepLockedForEdition(stepToDelete)) {
      setInlineError("Cette etape ne peut pas etre supprimee.");
      return;
    }
    const remaining = steps
      .filter((item) => item.id !== stepToDelete.id)
      .map((item, index) => ({ ...item, step: index + 1 }));
    setSteps(remaining);
    setSelectedStep(remaining[0]?.step ?? 1);
  };

  const handleSaveStepLabel = async (stepItem: Step) => {
    if (isStepLockedForEdition(stepItem)) {
      return;
    }
    const safeLabel = stepLabelDraft.trim();
    if (!safeLabel) {
      setEditStep(-1);
      setStepLabelDraft("");
      return;
    }
    updateStep(stepItem.id as number, (step) => ({
      ...step,
      nameStep: safeLabel,
      apiName: step.apiName ?? toApiStepName(safeLabel, step.step),
    }));
    setEditStep(-1);
    setStepLabelDraft("");
  };

  const handleSaveSelectedStepDescription = async (description: string) => {
    if (!selectedStepData?.id) {
      return;
    }
    updateStep(selectedStepData.id, (step) => ({
      ...step,
      description,
    }));
  };

  const handleUpdateStepVisibility = async (
    mode: VisibilityMode,
    projectTypeIds: number[],
  ) => {
    if (!selectedStepData?.id) return;
    updateStep(selectedStepData.id, (step) => ({
      ...step,
      visibilityMode: mode,
      visibilityProjectTypeIds: projectTypeIds,
    }));
  };

  const isDraftAutomaticThresholdMode =
    scoringSettingsDraft.decisionMode === "auto_threshold" ||
    scoringSettingsDraft.decisionMode === "auto_total";
  const scoringSettingsNeedRejectionThreshold =
    isDraftAutomaticThresholdMode ||
    scoringSettingsDraft.decisionMode === "manual_total";
  const scoringSettingsNeedAcceptanceThreshold = isDraftAutomaticThresholdMode;
  const parsedAcceptanceThreshold =
    scoringSettingsDraft.acceptancePercentage !== null &&
    scoringSettingsDraft.acceptancePercentage !== undefined
      ? Number(scoringSettingsDraft.acceptancePercentage)
      : NaN;
  const parsedRejectionThreshold =
    scoringSettingsDraft.rejectionPercentage !== null &&
    scoringSettingsDraft.rejectionPercentage !== undefined
      ? Number(scoringSettingsDraft.rejectionPercentage)
      : NaN;
  const isScoringSettingsValid =
    !scoringSettingsDraft.enabled ||
    (Boolean(scoringSettingsDraft.decisionMode) &&
      (!scoringSettingsNeedRejectionThreshold ||
        (Number.isFinite(parsedRejectionThreshold) &&
          parsedRejectionThreshold >= 0 &&
          parsedRejectionThreshold <= 100 &&
          (!scoringSettingsNeedAcceptanceThreshold ||
            (Number.isFinite(parsedAcceptanceThreshold) &&
              parsedAcceptanceThreshold >= 0 &&
              parsedAcceptanceThreshold <= 100 &&
              parsedRejectionThreshold <= parsedAcceptanceThreshold)))));

  const handleToggleCallFormScoring = (enabled: boolean) => {
    if (!enabled) {
      updateIntroStepOptions((options) => {
        const next = { ...options };
        next[CALL_FORM_SCORING_SETTINGS_KEY] = null;
        return next;
      });
      setScoringSettingsOpen(false);
      setScoringSettingsDraft({ enabled: false });
      return;
    }

    setScoringSettingsDraft((current) => ({
      enabled: true,
      decisionMode:
        (allowAdvancedScoringModes
          ? current.decisionMode
          : "auto_threshold") ??
        currentCallFormScoringSettings.decisionMode ??
        (allowAdvancedScoringModes ? undefined : "auto_threshold"),
      acceptancePercentage:
        current.acceptancePercentage ??
        currentCallFormScoringSettings.acceptancePercentage ??
        null,
      rejectionPercentage:
        current.rejectionPercentage ??
        currentCallFormScoringSettings.rejectionPercentage ??
        null,
    }));
    setScoringSettingsOpen(true);
  };

  const handleSaveScoringSettings = () => {
    if (!isScoringSettingsValid) {
      return;
    }

    const nextSettings: CallFormScoringSettings = {
      enabled: true,
      decisionMode: allowAdvancedScoringModes
        ? scoringSettingsDraft.decisionMode
        : "auto_threshold",
      acceptancePercentage: scoringSettingsNeedAcceptanceThreshold
        ? parsedAcceptanceThreshold
        : null,
      rejectionPercentage: scoringSettingsNeedRejectionThreshold
        ? parsedRejectionThreshold
        : null,
    };

    updateIntroStepOptions((options) => ({
      ...options,
      [CALL_FORM_SCORING_SETTINGS_KEY]: nextSettings,
    }));
    setScoringSettingsOpen(false);
  };

  const handleCreateField = async (payload: UpsertFieldPayload) => {
    if (!selectedStepData?.id) return;
    if (payload.type === "Map" && hasMapFieldInForm) {
      setInlineError("Un seul champ Carte est autorise pour tout le formulaire.");
      return;
    }
    if (payload.type === "funding" && hasFundingFieldInForm) {
      setInlineError("Un seul champ Financement est autorise pour tout le formulaire.");
      return;
    }

    const field: Field = {
      id: allocateId(),
      name: payload.name,
      label: payload.label,
      type: payload.type,
      required: payload.required,
      options: extractChoiceOptions(payload.options),
      scoring: extractScoringConfig(payload.options),
      decisionRules: extractDecisionRulesConfig(payload.options),
      nativeField: payload.nativeField,
      step: payload.step,
      parentId: payload.parentId ?? selectedStepData.id,
      sectionId: null,
      visibilityMode: "all",
      visibilityProjectTypeIds: [],
    };

    updateStep(selectedStepData.id, (step) => {
      const isSectionField = (step.sections ?? []).some(
        (section) => section.id === payload.parentId,
      );
      const nextField: Field = {
        ...field,
        parentId: isSectionField ? payload.parentId : selectedStepData.id,
        sectionId: isSectionField ? payload.parentId ?? null : null,
      };

      if (isSectionField) {
        return {
          ...step,
          sections: (step.sections ?? []).map((section) =>
            section.id === payload.parentId
              ? {
                  ...section,
                  fields: [...section.fields, nextField].map((item, index) => ({
                    ...item,
                    step: index + 1,
                  })),
                }
              : section,
          ),
        };
      }

      return {
        ...step,
        fields: [...step.fields, nextField].map((item, index) => ({
          ...item,
          step: index + 1,
        })),
      };
    });
    setInlineError(null);
  };

  const handleUpdateField = async (fieldId: number, payload: UpsertFieldPayload) => {
    const existingField = findFieldById(fieldId);
    if (
      payload.type === "Map" &&
      existingField?.type !== "Map" &&
      hasMapFieldInForm
    ) {
      setInlineError("Un seul champ Carte est autorise pour tout le formulaire.");
      return;
    }
    if (
      payload.type === "funding" &&
      existingField?.type !== "funding" &&
      hasFundingFieldInForm
    ) {
      setInlineError("Un seul champ Financement est autorise pour tout le formulaire.");
      return;
    }

    if (!selectedStepData?.id) return;
    updateStep(selectedStepData.id, (step) => {
      const existingRootField = step.fields.find((field) => field.id === fieldId);
      const existingSection = (step.sections ?? []).find((section) =>
        section.fields.some((field) => field.id === fieldId),
      );
      const isTargetSection = (step.sections ?? []).some(
        (section) => section.id === payload.parentId,
      );

      const updatedField = (field: Field, nextStep: number): Field => ({
        ...field,
        name: payload.name,
        label: payload.label,
        type: payload.type,
        required: payload.required,
        options: extractChoiceOptions(payload.options),
        scoring: extractScoringConfig(payload.options),
        decisionRules: extractDecisionRulesConfig(payload.options),
        nativeField: payload.nativeField,
        step: nextStep,
        parentId: isTargetSection ? payload.parentId : selectedStepData.id,
        sectionId: isTargetSection ? payload.parentId ?? null : null,
      });

      if (!existingRootField && !existingSection) {
        return step;
      }

      if (existingRootField && !isTargetSection) {
        return {
          ...step,
          fields: step.fields.map((field) =>
            field.id === fieldId ? updatedField(field, field.step ?? payload.step) : field,
          ),
        };
      }

      if (existingSection && isTargetSection && existingSection.id === payload.parentId) {
        return {
          ...step,
          sections: (step.sections ?? []).map((section) => ({
            ...section,
            fields: section.fields.map((field) =>
              field.id === fieldId ? updatedField(field, field.step ?? payload.step) : field,
            ),
          })),
        };
      }

      const movedField =
        existingRootField ??
        existingSection?.fields.find((field) => field.id === fieldId);

      if (!movedField) {
        return step;
      }

      const cleanedRootFields = step.fields.filter((field) => field.id !== fieldId);
      const cleanedSections = (step.sections ?? []).map((section) => ({
        ...section,
        fields: section.fields.filter((field) => field.id !== fieldId),
      }));

      if (isTargetSection) {
        return {
          ...step,
          fields: cleanedRootFields.map((field, index) => ({
            ...field,
            step: index + 1,
          })),
          sections: cleanedSections.map((section) =>
            section.id === payload.parentId
              ? {
                  ...section,
                  fields: [...section.fields, updatedField(movedField, payload.step)].map(
                    (field, index) => ({
                      ...field,
                      step: index + 1,
                    }),
                  ),
                }
              : {
                  ...section,
                  fields: section.fields.map((field, index) => ({
                    ...field,
                    step: index + 1,
                  })),
                },
          ),
        };
      }

      return {
        ...step,
        fields: [...cleanedRootFields, updatedField(movedField, payload.step)].map(
          (field, index) => ({
            ...field,
            step: index + 1,
          }),
        ),
        sections: cleanedSections.map((section) => ({
          ...section,
          fields: section.fields.map((field, index) => ({
            ...field,
            step: index + 1,
          })),
        })),
      };
    });
    setInlineError(null);
  };

  const handleDeleteField = async (fieldId: number) => {
    if (isProtectedStep(selectedStepData)) {
      const field = findFieldById(fieldId);
      const nativeKey = normalizeKey(field?.nativeField);
      if (nativeKey && PROTECTED_DESCRIPTIF_FIELDS.includes(nativeKey)) {
        setInlineError("Ce champ est obligatoire dans Descriptif du projet.");
        return;
      }
    }

    if (!selectedStepData?.id) return;
    updateStep(selectedStepData.id, (step) => ({
      ...step,
      fields: step.fields
        .filter((field) => field.id !== fieldId)
        .map((field, index) => ({ ...field, step: index + 1 })),
      sections: (step.sections ?? []).map((section) => ({
        ...section,
        fields: section.fields
          .filter((field) => field.id !== fieldId)
          .map((field, index) => ({ ...field, step: index + 1 })),
      })),
    }));
    setInlineError(null);
  };

  const handleCreateSection = async (title: string, description: string) => {
    if (!selectedStepData?.id) return;
    const safeTitle = title.trim();
    if (!safeTitle) return;

    const section: Section = {
      id: allocateId(),
      name: toApiStepName(safeTitle, (selectedStepData.sections?.length ?? 0) + 1),
      label: safeTitle,
      description: description.trim(),
      step: (selectedStepData.sections?.length ?? 0) + 1,
      fields: [],
    };

    updateStep(selectedStepData.id, (step) => ({
      ...step,
      sections: [...(step.sections ?? []), section].map((item, index) => ({
        ...item,
        step: index + 1,
      })),
    }));
    setInlineError(null);
  };

  const handleUpdateSection = async (sectionId: number, title: string, description: string) => {
    if (!selectedStepData?.id) return;
    const safeTitle = title.trim();
    if (!safeTitle) return;
    updateStep(selectedStepData.id, (step) => ({
      ...step,
      sections: (step.sections ?? []).map((section) =>
        section.id === sectionId
          ? {
              ...section,
              label: safeTitle,
              name: section.name || toApiStepName(safeTitle, section.step),
              description: description.trim(),
            }
          : section,
      ),
    }));
    setInlineError(null);
  };

  const handleDeleteSection = async (sectionId: number) => {
    if (!selectedStepData?.id) return;
    updateStep(selectedStepData.id, (step) => ({
      ...step,
      sections: (step.sections ?? [])
        .filter((section) => section.id !== sectionId)
        .map((section, index) => ({ ...section, step: index + 1 })),
    }));
    setInlineError(null);
  };

  const handleReorderItems = async (parentId: number, itemIds: number[]) => {
    if (!selectedStepData?.id) return false;
    updateStep(selectedStepData.id, (step) => {
      if (parentId === selectedStepData.id) {
        const rootItems = [
          ...step.fields.map((field) => ({ kind: "field" as const, id: field.id as number, field })),
          ...(step.sections ?? []).map((section) => ({
            kind: "section" as const,
            id: section.id as number,
            section,
          })),
        ];
        const ordered = itemIds
          .map((id) => rootItems.find((item) => item.id === id))
          .filter(Boolean) as typeof rootItems;
        return {
          ...step,
          fields: ordered
            .filter((item) => item.kind === "field")
            .map((item, index) => ({ ...item.field, step: index + 1 })),
          sections: ordered
            .filter((item) => item.kind === "section")
            .map((item, index) => ({ ...item.section, step: index + 1 })),
        };
      }

      return {
        ...step,
        sections: (step.sections ?? []).map((section) =>
          section.id === parentId
            ? {
                ...section,
                fields: itemIds
                  .map((id) => section.fields.find((field) => field.id === id))
                  .filter(Boolean)
                  .map((field, index) => ({ ...(field as Field), step: index + 1 })),
              }
            : section,
        ),
      };
    });
    return true;
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
        <div className="min-w-0">
          <p className="mb-1 text-sm font-semibold text-slate-800">
            {"Système de notation du formulaire"}
          </p>
          <p className="text-xs text-slate-600">
            Activez-le si vous souhaitez automatiser la validation, le rejet ou
            {" encadrer l'évaluation finale des candidatures."}
          </p>
          {currentCallFormScoringSettings.enabled &&
          currentCallFormScoringSettings.decisionMode ? (
            <p className="mt-2 text-xs font-medium text-[#1d5214]">
              {currentScoringUsesManualDecision
                ? `Décision finale laissée au porteur de projet, avec rejet automatique jusqu'à ${currentCallFormScoringSettings.rejectionPercentage ?? 0}%.`
                : `Validation automatique à partir de ${currentCallFormScoringSettings.acceptancePercentage ?? 0}% et rejet automatique jusqu'à ${currentCallFormScoringSettings.rejectionPercentage ?? 0}%.`}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3">
            <Switch
              id="call-form-scoring-enabled"
              checked={isCallFormScoringActive}
              onCheckedChange={handleToggleCallFormScoring}
            />
            <label
              htmlFor="call-form-scoring-enabled"
              className="m-0 text-sm font-medium text-slate-700"
            >
              Activer la notation
            </label>
          </div>
          {isCallFormScoringActive && (
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => {
                setScoringSettingsDraft(currentCallFormScoringSettings);
                setScoringSettingsOpen(true);
              }}
            >
              <Settings2 className="h-4 w-4" />
              {"Paramétrer"}
            </Button>
          )}
        </div>
      </div>

      <div
        className="grid gap-5"
        style={{ gridTemplateColumns: "minmax(260px, 300px) minmax(0, 1fr)" }}
      >
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
        <div className="mb-3 px-2 pt-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Etapes du formulaire
          </p>
          <p className="text-sm text-slate-600">{steps.length} etapes configurees</p>
          <p className="mt-1 text-xs text-slate-500">
            Glissez-deposez les etapes libres pour reorganiser leur ordre.
          </p>
        </div>

        {steps.map((item) => (
          <div
            key={item.id ?? item.step}
            onClick={() => setSelectedStep(item.step)}
            className={`mb-2 flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-all ${
              selectedStep === item.step
                ? "border-emerald-300 bg-emerald-50 shadow-sm"
                : "border-transparent bg-white hover:border-slate-200 hover:bg-slate-100"
            } ${dragOverStepId === item.id ? "ring-2 ring-emerald-200" : ""}`}
            role="button"
            tabIndex={0}
            draggable={Boolean(item.id) && !isStepLockedForMove(item)}
            onKeyDown={(e) => {
              if (e.target !== e.currentTarget) {
                return;
              }
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setSelectedStep(item.step);
              }
            }}
            onDragStart={(e) => {
              if (!item.id || isStepLockedForMove(item)) return;
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", String(item.id));
              setDraggedStepId(item.id ?? null);
            }}
            onDragOver={(e) => {
              if (!draggedStepId || draggedStepId === item.id || isStepLockedForMove(item)) {
                return;
              }
              e.preventDefault();
              setDragOverStepId(item.id ?? null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (!item.id) return;
              handleDropStep(item.id);
            }}
            onDragEnd={() => {
              setDraggedStepId(null);
              setDragOverStepId(null);
            }}
          >
            <div className="flex items-center gap-2">
              <GripVertical
                className={`h-4 w-4 text-slate-400 ${
                  isStepLockedForMove(item) ? "invisible" : ""
                }`}
              />
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold text-slate-700">
                {item.step}
              </div>
            </div>
            <div className="min-w-0 flex-1">
              {editStep === item.step ? (
                <Input
                  value={stepLabelDraft}
                  onChange={(e) => setStepLabelDraft(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="h-8"
                />
              ) : (
                <p className="line-clamp-2 text-sm font-medium text-slate-800">{item.nameStep}</p>
              )}
            </div>
            {!isStepLockedForEdition(item) && (
              <div className="flex items-center gap-1">
                {editStep === item.step ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleSaveStepLabel(item);
                    }}
                    className="rounded-md p-1 text-emerald-600 hover:bg-emerald-100"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditStep(item.step);
                      setStepLabelDraft(item.nameStep);
                    }}
                    className="rounded-md p-1 text-slate-600 hover:bg-slate-200"
                  >
                    <SquarePen className="h-4 w-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleDeleteStep(item);
                  }}
                  className="rounded-md p-1 text-red-600 hover:bg-red-50"
                >
                  <Trash className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        ))}

        <button
          className="mt-2 flex w-full items-center gap-3 rounded-xl border border-dashed border-emerald-300 bg-emerald-50 p-3 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-100"
          onClick={() => void handleAddStep()}
          type="button"
        >
          <CirclePlus size={20} />
          Ajouter une etape
        </button>
      </div>

      <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 border-b border-slate-100 pb-3">
          {inlineError && <p className="text-xs font-medium text-red-600">{inlineError}</p>}
          <p className="text-xs font-medium text-slate-500">
            Le formulaire reste temporaire tant que vous n'avez pas valide la creation du programme.
          </p>
        </div>

        <CreateForm
          data={fields}
          sections={sections}
          stepId={selectedStep}
          stepDbId={selectedStepData?.id}
          stepDescription={selectedStepData?.description}
          canEditStepDescription={!isStepLockedForEdition(selectedStepData)}
          allowScoring={isCallFormScoringActive}
          hasMapFieldInForm={hasMapFieldInForm}
          hasFundingFieldInForm={hasFundingFieldInForm}
          hiddenFieldKeys={
            isProtectedStep(selectedStepData) ? HIDDEN_CALL_DESCRIPTIF_FIELDS : []
          }
          busy={false}
          inlineError={inlineError}
          readOnly={false}
          isProtectedDescriptifStep={isProtectedStep(selectedStepData)}
          isFinancingPreviewStep={false}
          isIntroStep={isIntroStep(selectedStepData)}
          protectedFieldKeys={PROTECTED_DESCRIPTIF_FIELDS}
          onSaveStepDescription={handleSaveSelectedStepDescription}
          onCreateField={handleCreateField}
          onUpdateField={handleUpdateField}
          onDeleteField={handleDeleteField}
          onReorderItems={handleReorderItems}
          onCreateSection={handleCreateSection}
          onUpdateSection={handleUpdateSection}
          onDeleteSection={handleDeleteSection}
          stepName={selectedStepData?.nameStep}
          projectTypeOptions={projectTypeOptions}
          projectTypeOptionsLoading={projectTypeOptionsLoading}
          stepVisibilityMode={selectedStepData?.visibilityMode ?? "all"}
          stepVisibilityProjectTypeIds={selectedStepData?.visibilityProjectTypeIds ?? []}
          onUpdateStepVisibility={handleUpdateStepVisibility}
          onUpdateFieldVisibility={async (field, mode, projectTypeIds) => {
            if (!selectedStepData?.id) return;
            updateStep(selectedStepData.id, (step) => ({
              ...step,
              fields: step.fields.map((item) =>
                item.id === field.id
                  ? { ...item, visibilityMode: mode, visibilityProjectTypeIds: projectTypeIds }
                  : item,
              ),
              sections: (step.sections ?? []).map((section) => ({
                ...section,
                fields: section.fields.map((item) =>
                  item.id === field.id
                    ? { ...item, visibilityMode: mode, visibilityProjectTypeIds: projectTypeIds }
                    : item,
                ),
              })),
            }));
          }}
        />
      </div>
      </div>

      <Dialog open={scoringSettingsOpen} onOpenChange={setScoringSettingsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{"Paramétrer le système de notation"}</DialogTitle>
            <DialogDescription>
              Choisissez comment le score du candidat influencera la validation
              {" ou le rejet du projet dans cet appel à projets."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-medium text-slate-800">
                {"Mode de décision"}
              </p>
              <p className="mt-1 text-xs leading-5 text-slate-600">
                {"Le mode choisi sera dupliqué avec le modèle si ce formulaire"}
                {" d'appel est réutilisé pour un autre appel à projets."}
              </p>
              <div className="mt-4 space-y-3">
                <Select
                  value={scoringSettingsDraft.decisionMode}
                  onValueChange={(value: CallFormScoringDecisionMode) =>
                    setScoringSettingsDraft((current) => ({
                      ...current,
                      decisionMode: value,
                      acceptancePercentage:
                        value === "manual_total"
                          ? null
                          : current.acceptancePercentage,
                      rejectionPercentage: current.rejectionPercentage,
                    }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Sélectionner une stratégie de validation" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto_threshold">
                      Validation automatique basée uniquement sur les notes
                    </SelectItem>
                    {allowAdvancedScoringModes && (
                      <SelectItem value="manual_total">
                        {"Je décide moi-même après l'évaluation du score global"}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>

                {!allowAdvancedScoringModes && (
                  <p className="text-xs leading-5 text-slate-600">
                    Pour un appel à projets géré avec Verdeo, seule la validation
                    automatique sur les notes automatiques est disponible. La
                    validation finale reste ensuite du ressort de l&apos;administration.
                  </p>
                )}

                {!scoringSettingsDraft.decisionMode && (
                  <p className="text-xs text-red-600">
                    {"Veuillez sélectionner une stratégie de validation."}
                  </p>
                )}
              </div>
            </div>

            {scoringSettingsDraft.decisionMode && (
              <div className="rounded-2xl border border-[#dbe7d3] bg-[#f8fbf5] p-4">
                <p className="text-sm font-medium text-slate-800">
                  {isDraftAutomaticThresholdMode
                    ? "Validation ou rejet automatiques selon le score du formulaire"
                    : "Décision manuelle après calcul du score"}
                </p>
                <p className="mt-1 text-xs leading-5 text-slate-600">
                  {isDraftAutomaticThresholdMode
                    ? "Le projet est validé ou rejeté automatiquement selon les seuils configurés. Si le résultat se situe entre les deux seuils, la décision finale reste manuelle."
                    : "Le système calcule le score global. En dessous du seuil de rejet, le projet est rejeté automatiquement. Au-dessus, la décision finale reste au porteur de projet."}
                </p>

                {scoringSettingsNeedRejectionThreshold && (
                  <div className={`mt-4 grid gap-4 ${scoringSettingsNeedAcceptanceThreshold ? "md:grid-cols-2" : "md:grid-cols-1"}`}>
                    <div className="flex items-center gap-3">
                      <label className="text-sm font-medium text-slate-700">
                        Seuil minimal de rejet
                      </label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          value={
                            scoringSettingsDraft.rejectionPercentage === null ||
                            scoringSettingsDraft.rejectionPercentage === undefined
                              ? ""
                              : String(scoringSettingsDraft.rejectionPercentage)
                          }
                          onChange={(e) =>
                            setScoringSettingsDraft((current) => ({
                              ...current,
                              rejectionPercentage:
                                e.target.value === ""
                                  ? null
                                  : Number(e.target.value),
                            }))
                          }
                          className="w-28"
                        />
                        <span className="text-sm text-slate-600">%</span>
                      </div>
                    </div>
                    {scoringSettingsNeedAcceptanceThreshold && (
                      <div className="flex items-center gap-3">
                        <label className="text-sm font-medium text-slate-700">
                          Seuil minimal d&apos;acceptation
                        </label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            value={
                              scoringSettingsDraft.acceptancePercentage === null ||
                              scoringSettingsDraft.acceptancePercentage === undefined
                                ? ""
                                : String(scoringSettingsDraft.acceptancePercentage)
                            }
                            onChange={(e) =>
                              setScoringSettingsDraft((current) => ({
                                ...current,
                                acceptancePercentage:
                                  e.target.value === ""
                                    ? null
                                    : Number(e.target.value),
                              }))
                            }
                            className="w-28"
                          />
                          <span className="text-sm text-slate-600">%</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {scoringSettingsNeedRejectionThreshold &&
                  (!Number.isFinite(parsedRejectionThreshold) ||
                    (scoringSettingsNeedAcceptanceThreshold &&
                      !Number.isFinite(parsedAcceptanceThreshold))) && (
                  <p className="mt-2 text-xs text-red-600">
                    {scoringSettingsNeedAcceptanceThreshold
                      ? "Veuillez renseigner les seuils de rejet et d&apos;acceptation."
                      : "Veuillez renseigner le seuil de rejet."}
                  </p>
                )}
                {scoringSettingsNeedRejectionThreshold &&
                  (((scoringSettingsNeedAcceptanceThreshold &&
                    Number.isFinite(parsedAcceptanceThreshold) &&
                    (parsedAcceptanceThreshold < 0 || parsedAcceptanceThreshold > 100))) ||
                    (Number.isFinite(parsedRejectionThreshold) &&
                      (parsedRejectionThreshold < 0 || parsedRejectionThreshold > 100))) && (
                    <p className="mt-2 text-xs text-red-600">
                      Les seuils doivent être compris entre 0 et 100.
                    </p>
                  )}
                {scoringSettingsNeedAcceptanceThreshold &&
                  Number.isFinite(parsedAcceptanceThreshold) &&
                  Number.isFinite(parsedRejectionThreshold) &&
                  parsedRejectionThreshold > parsedAcceptanceThreshold && (
                    <p className="mt-2 text-xs text-red-600">
                      Le seuil de rejet doit être inférieur ou égal au seuil d&apos;acceptation.
                    </p>
                  )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setScoringSettingsOpen(false)}>
              Annuler
            </Button>
            <Button type="button" onClick={handleSaveScoringSettings} disabled={!isScoringSettingsValid}>
              Enregistrer le paramétrage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}






